import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { 
  Box, 
  Typography, 
  RadioGroup, 
  FormControlLabel, 
  Radio, 
  Paper, 
  Skeleton,
  Alert
} from '@mui/material';
import { useCart } from '../../contexts/CartContext';

// Shipping rate calculator based on product weight, dimensions, and shipping class
const calculateShippingRates = (items, destination, cartTotal = 0) => {
  // If no items in cart, return empty array
  if (!items || items.length === 0) {
    return [];
  }

  // Calculate total weight and determine largest shipping class
  let totalWeight = 0;
  let shippingClass = 'lightweight'; // Default
  let requiresSignature = false;

  // Skip calculation if no items or invalid destination
  if (!destination || !destination.address1 || !destination.city || !destination.state || !destination.zip) {
    return [];
  }

  items.forEach(item => {
    // Use default values if shipping info is not available
    const weight = item.shipping?.weight || 0.5;
    const itemShippingClass = item.shipping?.shippingClass || 'lightweight';
    const needsSignature = item.shipping?.requiresSignature || false;
    
    totalWeight += weight * item.quantity;
    
    // Upgrade shipping class if needed
    if (itemShippingClass === 'oversized') {
      shippingClass = 'oversized';
    } else if (itemShippingClass === 'standard' && shippingClass !== 'oversized') {
      shippingClass = 'standard';
    }

    // Check if any item requires signature
    if (needsSignature) {
      requiresSignature = true;
    }
  });

  // Base shipping rates
  const baseRates = {
    lightweight: {
      standard: { price: 5.99, days: '3-5' },
      expedited: { price: 9.99, days: '2-3' },
      overnight: { price: 24.99, days: '1' }
    },
    standard: {
      standard: { price: 8.99, days: '3-5' },
      expedited: { price: 14.99, days: '2-3' },
      overnight: { price: 34.99, days: '1' }
    },
    oversized: {
      standard: { price: 15.99, days: '5-7' },
      expedited: { price: 24.99, days: '3-5' },
      overnight: { price: 49.99, days: '1-2' }
    },
    free: {
      standard: { price: 0, days: '5-7' },
      expedited: { price: 9.99, days: '2-3' },
      overnight: { price: 24.99, days: '1' }
    }
  };

  // Weight surcharges
  let weightSurcharge = 0;
  if (totalWeight > 32) {
    weightSurcharge = Math.ceil((totalWeight - 32) / 16) * 5;
  }

  // Signature surcharge
  const signatureSurcharge = requiresSignature ? 3.99 : 0;

  // Calculate final rates
  const rates = [];
  
  // Standard shipping
  rates.push({
    id: 'standard',
    name: 'Standard Shipping',
    description: `Delivery in ${baseRates[shippingClass].standard.days} business days`,
    price: baseRates[shippingClass].standard.price + weightSurcharge + signatureSurcharge,
    estimatedDays: baseRates[shippingClass].standard.days
  });

  // Expedited shipping
  rates.push({
    id: 'expedited',
    name: 'Expedited Shipping',
    description: `Delivery in ${baseRates[shippingClass].expedited.days} business days`,
    price: baseRates[shippingClass].expedited.price + weightSurcharge + signatureSurcharge,
    estimatedDays: baseRates[shippingClass].expedited.days
  });

  // Overnight shipping
  rates.push({
    id: 'overnight',
    name: 'Overnight Shipping',
    description: `Delivery in ${baseRates[shippingClass].overnight.days} business day(s)`,
    price: baseRates[shippingClass].overnight.price + weightSurcharge + signatureSurcharge,
    estimatedDays: baseRates[shippingClass].overnight.days
  });

  // Free shipping for orders over $150 (except oversized items)
  // Use cartTotal directly if available, otherwise calculate from items
  const subtotal = cartTotal || items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  if (subtotal >= 150 && shippingClass !== 'oversized') {
    rates[0].price = 0;
    rates[0].name = 'Free Shipping';
    rates[0].description = `Free shipping on orders over $150 - Delivery in ${baseRates[shippingClass].standard.days} business days`;
  }

  return rates;
};

const ShippingOptions = ({ destination, onShippingChange }) => {
  const { cart } = useCart();
  const cartItems = cart?.items || [];
  const cartTotal = cart?.total || 0;
  const [shippingRates, setShippingRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Effect to calculate shipping rates when cart or destination changes
  useEffect(() => {
    // Define a function to calculate shipping rates
    const calculateRates = () => {
      setLoading(true);
      setError(null);
      
      try {
        // Calculate shipping rates directly without async/await
        const rates = calculateShippingRates(cartItems, destination, cartTotal);
        
        setShippingRates(rates);
        
        // Select the first rate by default if no rate is selected or if the selected rate is not in the new rates
        if (rates.length > 0) {
          if (!selectedRate) {
            setSelectedRate(rates[0].id);
          } else {
            // Check if the previously selected rate is still available
            const rateStillExists = rates.some(rate => rate.id === selectedRate);
            if (!rateStillExists) {
              setSelectedRate(rates[0].id);
            }
          }
        }
      } catch (err) {
        console.error('Error calculating shipping rates:', err);
        setError('Failed to calculate shipping options. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    // Only calculate shipping rates if we have items in the cart and a destination
    if (cartItems.length > 0 && destination && 
        destination.address1 && destination.city && 
        destination.state && destination.zip) {
      calculateRates();
    } else if (cartItems.length === 0) {
      setShippingRates([]);
      setSelectedRate(null);
      setLoading(false);
    } else {
      // If we have items but no destination, just show loading false
      setLoading(false);
    }
  }, [cartItems, destination, cartTotal]);  // Remove selectedRate and onShippingChange from dependencies
  
  // Separate effect to handle shipping option changes
  useEffect(() => {
    if (selectedRate && shippingRates.length > 0) {
      const selectedShippingRate = shippingRates.find(rate => rate.id === selectedRate);
      if (selectedShippingRate && onShippingChange) {
        onShippingChange(selectedShippingRate);
      }
    } else if (shippingRates.length === 0 && onShippingChange) {
      onShippingChange(null);
    }
  }, [selectedRate, shippingRates, onShippingChange]);

  const handleShippingChange = (event) => {
    const rateId = event.target.value;
    setSelectedRate(rateId);
    
    const selectedShippingRate = shippingRates.find(rate => rate.id === rateId);
    onShippingChange(selectedShippingRate);
  };

  if (cartItems.length === 0) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Shipping Options
        </Typography>
        <Alert severity="info">
          Add items to your cart to see shipping options.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Shipping Options
        </Typography>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Skeleton height={30} width="60%" />
          <Skeleton height={24} width="40%" sx={{ mt: 1 }} />
          <Skeleton height={30} width="60%" sx={{ mt: 2 }} />
          <Skeleton height={24} width="40%" sx={{ mt: 1 }} />
          <Skeleton height={30} width="60%" sx={{ mt: 2 }} />
          <Skeleton height={24} width="40%" sx={{ mt: 1 }} />
        </Paper>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Shipping Options
        </Typography>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  try {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Shipping Options
        </Typography>
        
        {!destination || !destination.address1 || !destination.city || !destination.state || !destination.zip ? (
          <Alert severity="info">
            Please enter your shipping address to see shipping options.
          </Alert>
        ) : loading ? (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Skeleton height={30} width="60%" />
            <Skeleton height={24} width="40%" sx={{ mt: 1 }} />
            <Skeleton height={30} width="60%" sx={{ mt: 2 }} />
            <Skeleton height={24} width="40%" sx={{ mt: 1 }} />
          </Paper>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : shippingRates.length === 0 ? (
          <Alert severity="warning">
            No shipping options available for your location.
          </Alert>
        ) : (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <RadioGroup
              value={selectedRate}
              onChange={handleShippingChange}
            >
              {shippingRates.map((rate) => (
                <FormControlLabel
                  key={rate.id}
                  value={rate.id}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">
                        {rate.name} - ${rate.price.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {rate.description}
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 1 }}
                />
              ))}
            </RadioGroup>
          </Paper>
        )}
      </Box>
    );
  } catch (error) {
    console.error('Error rendering ShippingOptions:', error);
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Shipping Options
        </Typography>
        <Alert severity="error">
          An error occurred while calculating shipping options. Please try again later.
        </Alert>
      </Box>
    );
  }
};

ShippingOptions.propTypes = {
  destination: PropTypes.shape({
    address1: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
    country: PropTypes.string
  }),
  onShippingChange: PropTypes.func
};

ShippingOptions.defaultProps = {
  destination: null,
  onShippingChange: () => {}
};

export default ShippingOptions;
